/**
 * Articles live as Markdown files in ./articles/*.md with YAML-ish front-matter.
 * Adding a post = adding a file. Nothing else to touch.
 *
 * Front-matter keys: title, description, date (YYYY-MM-DD), keyword, tags [..],
 * cta (nrr-grr | expansion-headroom | tool-tax | deal-pricing | pilot),
 * pillar (true for the cluster hub), related [slug, ..].
 *
 * Scheduling: a post whose date is in the future is not listed, not routable
 * and not in the sitemap/RSS (scripts/gen-seo.js applies the same rule at
 * build time). Publishing on a date therefore needs no deploy for the site
 * itself; re-run the build to refresh sitemap.xml/blog.xml when it lands.
 */

// Webpack: import every .md as a raw string (craco.config.js adds the asset/source rule).
const ctx = require.context("./articles", false, /\.md$/);

export const AUTHOR = "Team Postern";

function parseList(v) {
  return v.replace(/^\[|\]$/g, "").split(",").map((s) => s.trim().replace(/^["']|["']$/g, "")).filter(Boolean);
}

function parseFrontMatter(raw) {
  const m = raw.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (!m) return [{}, raw];
  const meta = {};
  for (const line of m[1].split("\n")) {
    const kv = line.match(/^([a-zA-Z_]+):\s*(.*)$/);
    if (!kv) continue;
    let v = kv[2].trim();
    if (v.startsWith("[")) v = parseList(v);
    else {
      if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) v = v.slice(1, -1).replace(/\\"/g, '"');
      if (v === "true") v = true; else if (v === "false") v = false;
    }
    meta[kv[1]] = v;
  }
  return [meta, m[2]];
}

const words = (md) => md.replace(/[#*_>`\-\[\]()]/g, " ").split(/\s+/).filter(Boolean).length;

export const ALL_ARTICLES = ctx.keys().map((k) => {
  const slug = k.replace(/^\.\//, "").replace(/\.md$/, "");
  const raw = ctx(k);
  const [meta, body] = parseFrontMatter(typeof raw === "string" ? raw : raw.default);
  const w = words(body);
  return { slug, ...meta, tags: meta.tags || [], related: meta.related || [], body, words: w, minutes: Math.max(1, Math.round(w / 230)) };
}).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

// Published = dated today or earlier (UTC date compare on the YYYY-MM-DD string).
export const todayISO = () => new Date().toISOString().slice(0, 10);
export const isPublished = (a) => !!a.date && a.date <= todayISO();
export const publishedArticles = () => ALL_ARTICLES.filter(isPublished);
export const findArticle = (slug) => ALL_ARTICLES.find((a) => a.slug === slug && isPublished(a)) || null;
export const pillar = () => publishedArticles().find((a) => a.pillar) || null;

// End-of-post CTA: one, restrained, relevant.
export const CTA_MAP = {
  "nrr-grr": { label: "Work out your NRR and GRR", to: "/calculators/nrr-grr", note: "Four inputs, one minute, nothing stored." },
  "expansion-headroom": { label: "Size the whitespace in your own base", to: "/calculators/expansion-headroom", note: "A rough number for unsold product across accounts that already trust you." },
  "renewal-uplift": { label: "See what an uplift is worth across your book", to: "/calculators/renewal-uplift", note: "Three years of a given uplift versus going out flat. Nothing stored." },
  "discount-leakage": { label: "Estimate your discount leakage", to: "/calculators/discount-leakage", note: "Policy discount versus realised discount, across a year of bookings." },
  "tool-tax": { label: "Calculate your coordination cost", to: "/calculators/tool-tax", note: "Hours a week lost to the relay, at your loaded cost." },
  "deal-pricing": { label: "Try the approval rule live", to: "/#calculator", note: "The product's own pricing and policy functions, not a copy." },
  pilot: { label: "Talk to us about a pilot", to: "/pilot", note: "10 days, no card, your first real deal priced in session one." },
};
